import { ReactElement } from 'react';
import { classNames } from '../../utils';

interface Props {
  color?: 'gray' | 'red' | 'yellow' | 'green' | 'blue';
  title?: string | ReactElement;
  rounded?: boolean;
  hideDot?: boolean;
  showBorder?: boolean;
  showBackground?: boolean;
  size?: 'x-small' | 'small' | 'normal' | 'medium' | 'large';
  showDeleteButton?: boolean;
  onDelete?: () => void;
}

export default function Badge({
  color = 'green',
  title,
  rounded = true,
  hideDot,
  showBorder,
  size = 'normal',
  showDeleteButton,
  showBackground = true,
  onDelete,
}: Props) {
  return (
    <>
      <span
        className={classNames(
          'inline-flex items-center gap-x-1.5 whitespace-nowrap bg-gray-100 text-xs font-medium text-gray-600',

          rounded ? 'rounded-full' : 'rounded-md',
          showBorder ? 'ring-1 ring-inset' : '',

          color === 'yellow' &&
            'bg-yellow-100 text-yellow-700 ring-yellow-700/10',
          color === 'green' && 'bg-green-100 text-green-700 ring-green-700/10',
          color === 'red' && 'bg-red-100 text-red-700 ring-red-700/10',
          color === 'blue' && 'bg-blue-50 text-blue-700 ring-blue-600/20',
          color === 'gray' && 'bg-gray-100 text-gray-700 ring-gray-700/10',

          !showBackground && 'bg-opacity-0',

          size === 'x-small' ? 'px-1.5 py-0.5' : '',
          size === 'small' ? 'px-1.5 py-1' : '',
          size === 'normal' ? 'px-2 py-1.5' : '',
          size === 'medium' ? 'px-2.5 py-1.5' : '',
          size === 'large' ? 'px-3 py-1.5' : '',
        )}
      >
        {hideDot ? (
          <></>
        ) : (
          <svg
            className={classNames(
              'h-1.5 w-1.5',
              color === 'yellow' && 'fill-yellow-500',
              color === 'green' && 'fill-green-500',
              color === 'red' && 'fill-red-500',
              color === 'blue' && 'fill-blue-500',
              color === 'gray' && 'fill-gray-500',
            )}
            viewBox="0 0 6 6"
            aria-hidden="true"
          >
            <circle cx={3} cy={3} r={3} />
          </svg>
        )}
        {title}
        {showDeleteButton ? (
          <button
            type="button"
            className="group relative -mr-1 h-3.5 w-3.5 rounded-full hover:bg-gray-500/20"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
          >
            <span className="sr-only">Remove</span>
            <svg
              viewBox="0 0 14 14"
              className="h-3.5 w-3.5 stroke-gray-700/50 group-hover:stroke-gray-700/75"
            >
              <path d="M4 4l6 6m0-6l-6 6" />
            </svg>
            <span className="absolute -inset-1" />
          </button>
        ) : (
          <></>
        )}
      </span>
    </>
  );
}
